import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { DubPipelineDiagram } from "./DubPipelineDiagram";
import { CaseStudyCTA } from "../CaseStudyCTA";
import { SectionClouds } from "@/components/landing";
import dubcircusImg from "@/assets/dubcircus.webp";

const LIVE_URL = "https://dubcircus.io";

const STACK = [
  "Voice separation",
  "Web Audio",
  "TypeScript",
  "React",
  "Supabase Realtime",
  "Twitch chat",
];

interface Metric {
  value?: string;
  label: string;
}

export function DubcircusCaseStudy() {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const metrics = t("work.dubcircus.section3.metrics", {
    returnObjects: true,
  }) as Metric[];
  const metric4Before = t("work.dubcircus.section3.metric4Before");
  const metric4After = t("work.dubcircus.section3.metric4After");

  return (
    <article ref={ref} className="frame bg-page-bg">
      <SectionClouds variant="case-study" />
      <div className="container-narrow section-pad relative z-10 !pt-10">
        {/* Hero */}
        <header
          className={cn(
            "transition-all duration-700",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          <div className="mx-auto max-w-[760px]">
            <div>
              <Link
                to={localizedHref("/work")}
                className="mono inline-flex items-center gap-1 text-[11px] tracking-[0.16em] text-ink-3 uppercase transition-colors hover:text-ink"
              >
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
                {t("work.back")}
              </Link>
            </div>
            <div className="mt-8">
              <span className="eyebrow">
                <span className="num">{t("work.dubcircus.caseStudyEyebrow")}</span> dubcircus
              </span>
            </div>
            <h1 className="mt-4 text-[clamp(36px,5.6vw,68px)] leading-[1.04] font-medium tracking-[-0.035em] text-ink">
              {t("work.dubcircus.title")}
              <br />
              <span className="serif-italic text-accent">{t("work.dubcircus.titleEm")}</span>
            </h1>
            <div className="mono mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] tracking-[0.16em] text-ink-3 uppercase">
              <a
                href={LIVE_URL}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-1 text-ink-2 transition-colors hover:text-ink"
              >
                dubcircus.io
                <ArrowUpRight className="h-3 w-3" strokeWidth={2.5} />
              </a>
              <span aria-hidden="true">·</span>
              <span>{t("work.dubcircus.metaOwn")}</span>
              <span aria-hidden="true">·</span>
              <span>{t("work.dubcircus.metaShipped")}</span>
            </div>
            <p className="mt-5 max-w-[68ch] text-[13px] leading-[1.6] text-ink-3 italic">
              {t("work.dubcircus.disclosure")}
            </p>
            <div className="mt-6 flex flex-wrap gap-1.5">
              {STACK.map((s) => (
                <span
                  key={s}
                  className="mono rounded-full border border-line bg-surface px-2 py-1 text-[11px] tracking-tight text-ink-2"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* Hero visual — dubcircus landing page */}
          <div className="relative mt-12 aspect-[16/8] overflow-hidden rounded-[22px] border border-line bg-surface-2">
            <img
              src={dubcircusImg}
              alt={t("work.dubcircus.heroAlt")}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
          </div>
        </header>

        {/* Section 01 — The problem */}
        <section className="mt-20 lg:mt-28">
          <div className="mx-auto max-w-[760px]">
            <span className="eyebrow">
              <span className="num">01 —</span> {t("work.dubcircus.section1.eyebrow")}
            </span>
            <h2 className="mt-3 text-[clamp(30px,4vw,46px)] leading-[1.1] font-medium tracking-[-0.025em] text-ink">
              {t("work.dubcircus.section1.titleLead")}{" "}
              <span className="serif-italic">{t("work.dubcircus.section1.titleEm")}</span>
            </h2>
            <div className="mt-7 space-y-5 text-[16px] leading-[1.65] text-ink-2">
              <p>{t("work.dubcircus.section1.p1")}</p>
              <p>{t("work.dubcircus.section1.p2")}</p>
              <p>
                {t("work.dubcircus.section1.p3a")}
                <strong className="font-medium text-ink">
                  {t("work.dubcircus.section1.p3strong")}
                </strong>
                {t("work.dubcircus.section1.p3b")}
              </p>
            </div>
          </div>
        </section>

        {/* Section 02 — What we built */}
        <section className="mt-20 lg:mt-28">
          <div className="mx-auto max-w-[760px]">
            <span className="eyebrow">
              <span className="num">02 —</span> {t("work.dubcircus.section2.eyebrow")}
            </span>
            <h2 className="mt-3 text-[clamp(30px,4vw,46px)] leading-[1.1] font-medium tracking-[-0.025em] text-ink">
              {t("work.dubcircus.section2.titleLead")}{" "}
              <span className="serif-italic">{t("work.dubcircus.section2.titleEm")}</span>
            </h2>
            <p className="mt-5 max-w-[58ch] text-[clamp(15px,1.4vw,17px)] leading-[1.55] text-ink-2">
              <Trans
                i18nKey="work.dubcircus.section2.lede"
                components={{
                  // Not "link": the Trans parser treats <link> as a void HTML tag.
                  site: (
                    <a
                      href={LIVE_URL}
                      target="_blank"
                      rel="noopener"
                      className="font-medium text-accent underline decoration-accent/40 underline-offset-4 transition-colors hover:decoration-accent"
                    />
                  ),
                }}
              />
            </p>
          </div>

          <div className="mt-12">
            <DubPipelineDiagram />
          </div>

          <div className="mx-auto mt-12 max-w-[760px] space-y-5 text-[16px] leading-[1.65] text-ink-2">
            <p>
              <strong className="font-medium text-ink">
                {t("work.dubcircus.section2.p1Strong")}
              </strong>
              {t("work.dubcircus.section2.p1")}
            </p>
            <p>
              <strong className="font-medium text-ink">
                {t("work.dubcircus.section2.p2Strong")}
              </strong>
              {t("work.dubcircus.section2.p2")}
            </p>
            <p>
              <strong className="font-medium text-ink">
                {t("work.dubcircus.section2.p3Strong")}
              </strong>
              {t("work.dubcircus.section2.p3")}
            </p>
            <p>
              <strong className="font-medium text-ink">
                {t("work.dubcircus.section2.p4Strong")}
              </strong>
              {t("work.dubcircus.section2.p4")}
            </p>
          </div>
        </section>

        {/* Section 03 — What changed */}
        <section className="mt-20 lg:mt-28">
          <div className="mx-auto max-w-[760px]">
            <span className="eyebrow">
              <span className="num">03 —</span> {t("work.dubcircus.section3.eyebrow")}
            </span>
            <h2 className="mt-3 text-[clamp(30px,4vw,46px)] leading-[1.1] font-medium tracking-[-0.025em] text-ink">
              {t("work.dubcircus.section3.titleLead")}{" "}
              <span className="serif-italic">{t("work.dubcircus.section3.titleEm")}</span>
            </h2>
          </div>

          {/* Metrics strip */}
          <div className="mt-12 grid grid-cols-2 border-y border-line lg:grid-cols-4">
            <Metric value={metrics[0]!.value!} label={metrics[0]!.label} />
            <Metric value={metrics[1]!.value!} label={metrics[1]!.label} border />
            <Metric value={metrics[2]!.value!} label={metrics[2]!.label} borderTop />
            <Metric
              value={
                <>
                  {metric4Before} <span className="text-accent">→</span> {metric4After}
                </>
              }
              label={metrics[3]!.label}
              border
              borderTop
            />
          </div>

          <div className="mx-auto mt-12 max-w-[760px] space-y-5 text-[16px] leading-[1.65] text-ink-2">
            <p>{t("work.dubcircus.section3.p1")}</p>
            <p>{t("work.dubcircus.section3.p2")}</p>
            <p>{t("work.dubcircus.section3.p3")}</p>
          </div>
        </section>

        <CaseStudyCTA />
      </div>
    </article>
  );
}

interface MetricProps {
  value: React.ReactNode;
  label: string;
  border?: boolean;
  borderTop?: boolean;
}

function Metric({ value, label, border, borderTop }: MetricProps) {
  return (
    <div
      className={cn(
        "p-7 lg:p-9",
        border && "border-line lg:border-l",
        borderTop && "border-t border-line lg:border-t-0",
      )}
    >
      <div className="text-[clamp(36px,4.6vw,56px)] leading-none font-medium tracking-[-0.025em] text-ink">
        {value}
      </div>
      <div className="mt-4 max-w-[26ch] text-[13px] leading-[1.5] text-ink-2">{label}</div>
    </div>
  );
}
